/**
 * IBAS Current Students data — single source of truth for the /current-students
 * page (International Students Office · Events & Social Programmes · Campus
 * Thalwil Events).
 *
 * SOURCING (ibas-brand skill, HARD rule): every institutional fact here comes
 * ONLY from the branding folder, https://ibas.edu.eu, or the user — nothing is
 * invented. Where a fact is not verified it is LEFT OUT, never guessed. Notably:
 *   • There is no dedicated "Current Students" landing page live — content is
 *     drawn from the real support pages below.
 *   • No student handbook, named clubs/societies, or IT-support contact were
 *     found — so none are asserted here.
 *   • A role-based alias (campus.manager@ibas.edu.eu) is used for the On-Site
 *     Campus Manager rather than a personal address, per compliance guidance.
 *
 * Sources:
 *   • International Students Office: https://ibas.edu.eu/international-students-office/
 *   • Events & Social Programmes:   https://ibas.edu.eu/events-social-programmes/
 *   • Campus Thalwil Events:        https://ibas.edu.eu/ibas-events/ (+ /news/)
 *   • Campus + contact:             https://ibas.edu.eu/campus-thalwil/ , /contact-us/
 *
 * Claims carry scope where practical (e.g. "within 7 days", "within three
 * months"). Dates are reproduced as published; all listed campus events are
 * past and are framed as recent highlights, never as upcoming.
 */

import {
  Briefcase,
  Building,
  Building2,
  BookOpen,
  Cake,
  Clock,
  Compass,
  CreditCard,
  ExternalLink,
  FileCheck,
  Handshake,
  HeartPulse,
  Home,
  Languages,
  Lightbulb,
  LogOut,
  Mail,
  MapPin,
  PartyPopper,
  Phone,
  PlaneTakeoff,
  Presentation,
  Rocket,
  type LucideIcon,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
 * 1 · INTERNATIONAL STUDENTS OFFICE  (source: /international-students-office/)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const ISO_LEDE =
  'The International Students Office helps you settle into life in Switzerland — from your visa and arrival to housing, insurance and everyday practicalities — so you can focus on your studies.';

export interface IsoService {
  name: string;
  description: string;
  Icon: LucideIcon;
}

export const ISO_SERVICES: readonly IsoService[] = [
  {
    name: 'Student visa & immigration',
    description:
      'Guidance on Swiss student-visa requirements and documentation, with support through the visa application and prolongation for continuing students.',
    Icon: FileCheck,
  },
  {
    name: 'Arrival & registration',
    description:
      'Assistance with immigration upon arrival — Swiss law requires registration within 7 days of arriving.',
    Icon: PlaneTakeoff,
  },
  {
    name: 'Municipality (Gemeinde) registration',
    description: 'Help completing your registration with the local Gemeinde.',
    Icon: Building2,
  },
  {
    name: 'Accommodation',
    description:
      'Help finding and renting a room, including through our housing partner Studentenhaus-WOKO.',
    Icon: Home,
  },
  {
    name: 'Health insurance',
    description:
      'Guidance on purchasing Swiss health insurance — required within three months of arrival — and on local medical facilities.',
    Icon: HeartPulse,
  },
  {
    name: 'Banking & travel',
    description: 'Support opening a bank account and arranging a local travel card.',
    Icon: CreditCard,
  },
  {
    name: 'Translator services',
    description: 'Translation support available on request.',
    Icon: Languages,
  },
  {
    name: 'Local orientation',
    description: 'Information on local culture, customs and daily life in and around Thalwil.',
    Icon: Compass,
  },
  {
    name: 'Exit management',
    description: 'Practical support for students departing at the end of their studies.',
    Icon: LogOut,
  },
];

export interface IsoContactRow {
  label: string;
  value: string;
  /** Optional link target (tel:, mailto: or https:). */
  href?: string;
  Icon: LucideIcon;
}

export const ISO_CONTACT: readonly IsoContactRow[] = [
  {
    label: 'Address',
    value: 'Gewerbestrasse 14, 8800 Thalwil, Switzerland',
    Icon: MapPin,
  },
  {
    label: 'Phone',
    value: '+41 76 623 7895',
    href: 'tel:+41766237895',
    Icon: Phone,
  },
  {
    label: 'Office hours',
    value: 'Monday & Thursday, 09:30–17:00',
    Icon: Clock,
  },
  {
    label: 'Email',
    value: 'admissions@ibas.edu.eu',
    href: 'mailto:admissions@ibas.edu.eu',
    Icon: Mail,
  },
  {
    label: 'Student portal (OpenSIS)',
    value: 'ibas.opensis.com',
    href: 'https://ibas.opensis.com/',
    Icon: ExternalLink,
  },
];

/** The on-site campus manager (role alias used in place of a personal address). */
export const ISO_CAMPUS_MANAGER = {
  name: 'Beata Hubnerova',
  role: 'On-Site Campus Manager',
  email: 'campus.manager@ibas.edu.eu',
} as const;

/** Current students are asked to communicate through official channels. */
export const ISO_COMMS_NOTE =
  'Current students communicate with IBAS through the OpenSIS portal or their school-issued email.';

/** External partner resources referenced by the office (verified outbound links). */
export interface IsoPartner {
  name: string;
  detail: string;
  href: string;
}

export const ISO_PARTNERS: readonly IsoPartner[] = [
  {
    name: 'Studentenhaus-WOKO',
    detail: 'Student-housing partner — over 3,500 rooms across 65 properties.',
    href: 'https://www.woko.ch/en/liegenschaften',
  },
  {
    name: 'Airport Medical Center',
    detail: 'A local medical facility available to students.',
    href: 'https://www.airportmedicalcenter.ch/home-en.html',
  },
  {
    name: 'Authorised banks (FINMA)',
    detail: 'List of authorised Swiss banks for opening an account (PDF).',
    href: 'https://ibas.edu.eu/wp-content/uploads/2022/04/Authorised_Banks-FINMA-07-2021.pdf',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
 * 2 · EVENTS & SOCIAL PROGRAMMES  (source: /events-social-programmes/ + linked)
 * ═══════════════════════════════════════════════════════════════════════════ */

export const EVENTS_LEDE =
  'Beyond the classroom, IBAS runs workshops, company visits, guest speakers and social gatherings, alongside dedicated programmes that support your learning, your career and your ideas.';

/** The recurring event formats named on the source page. */
export const EVENT_FORMATS: readonly string[] = [
  'Workshops',
  'Company visits',
  'Guest speakers',
  'Social gatherings',
];

export interface StudentProgramme {
  name: string;
  description: string;
  points: readonly string[];
  Icon: LucideIcon;
}

export const STUDENT_PROGRAMMES: readonly StudentProgramme[] = [
  {
    name: 'Centre for Learning Excellence',
    description: 'Dedicated to supporting your academic success at IBAS.',
    points: [
      'Personalised guidance and skills workshops',
      'Study-skills and academic-writing support, diploma to doctoral',
      'Accessibility accommodations, arranged via the Registrar’s Office',
    ],
    Icon: BookOpen,
  },
  {
    name: 'Business Incubator',
    description:
      'A structured, professional environment for motivated entrepreneurs to develop and refine their business ideas.',
    points: [
      'Free desk space for one year, with mentor support',
      'No participation fees, and no equity taken by IBAS',
      'Workshops from business planning to digital marketing and investors',
    ],
    Icon: Rocket,
  },
  {
    name: 'Career Centre',
    description:
      'Personalised career advising, workshops and networking — with access for life for graduates.',
    points: [
      'CV, cover-letter and letters-of-recommendation support',
      'Interview preparation and skills',
      'Job-search strategy and LinkedIn networking',
    ],
    Icon: Briefcase,
  },
  {
    name: 'Alumni network',
    description: 'Graduation is not the end of the relationship — it is the beginning of a lasting professional connection.',
    points: [
      'Guest-speaking and on-site engagement in Zurich',
      'International networking through My Global World (MGW)',
      'Graduate verification platform for degree confirmation',
    ],
    Icon: Handshake,
  },
];

/** Some workshops run only above a minimum registration (source-stated). */
export const EVENTS_WORKSHOP_NOTE =
  'Some workshops run only once a minimum number of students have registered.';


/* ═══════════════════════════════════════════════════════════════════════════
 * 3 · CAMPUS THALWIL EVENTS  (source: /ibas-events/ + /news/)
 *     All listed events are PAST — presented as recent on-site highlights.
 * ═══════════════════════════════════════════════════════════════════════════ */

export const CAMPUS_EVENTS_LEDE =
  'On-site life at the Thalwil campus runs on gatherings, workshops, company visits and guest speakers. A look at some recent moments — with new dates announced through the year.';

export interface CampusEvent {
  title: string;
  /** Date as published on the source (past events). */
  date: string;
  /** Event format label. */
  kind: string;
  description: string;
  Icon: LucideIcon;
}

/** Recent on-site events, most recent first (source: /ibas-events/). */
export const CAMPUS_EVENTS: readonly CampusEvent[] = [
  {
    title: 'On-Site MBA Campus Gathering',
    date: '31 March 2026',
    kind: 'Social',
    description:
      'A small on-campus gathering to mark an important milestone for our MBA students, with a special visit from President Martin Nielsen.',
    Icon: PartyPopper,
  },
  {
    title: 'Understanding Investors',
    date: '16 October 2025',
    kind: 'Workshop',
    description:
      'A workshop led by Vice President Julie Nielsen, exploring how to approach and understand investors.',
    Icon: Lightbulb,
  },
  {
    title: 'Company Visit: Barfly’z',
    date: '17 September 2025',
    kind: 'Company visit',
    description:
      'A behind-the-scenes look at one of the city’s well-known hospitality venues, for students on the relevant specialisations.',
    Icon: Building,
  },
  {
    title: 'Cake by the Park',
    date: '2 May 2025',
    kind: 'Social',
    description:
      'A relaxed social event where IBAS students gathered outdoors to enjoy sweet treats together.',
    Icon: Cake,
  },
  {
    title: 'Guest Speaker: IBAS alumna Chisa',
    date: '1 May 2025',
    kind: 'Guest speaker',
    description:
      'IBAS alumna Sirithakorn Srinitioj (“Chisa”), an entrepreneur from Thailand, returned to share her experience of studying and building a business in Switzerland.',
    Icon: Presentation,
  },
];

/** Verbatim moments from the IBAS news, tied to on-site campus life. */
export interface CampusMoment {
  title: string;
  date: string;
  /** Reproduced verbatim from the source news post. */
  quote: string;
}

export const CAMPUS_MOMENTS: readonly CampusMoment[] = [
  {
    title: 'A Small Celebration, A Big Step Forward',
    date: '1 April 2026',
    quote:
      'Sometimes it is these smaller, informal moments that stay with people the most.',
  },
  {
    title: 'Welcome, March 2025 On-Site Students!',
    date: '14 April 2025',
    quote:
      'During the orientation, students had the chance to meet our faculty, explore the campus in Thalwil, and get a head start on their academic journey.',
  },
];

/** Where on-site events are hosted (source: campus + events pages). */
export const CAMPUS_EVENTS_ADDRESS = 'Gewerbestrasse 14, 8800 Thalwil, Switzerland';

/** Graduation context (source: alumni page). */
export const GRADUATION_NOTE =
  'IBAS also holds graduation ceremonies for its community, including on-site ceremonies in Zurich.';
